import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import type { DynamicEntityRow } from '../../../../api/strapiDynamicEntity';
import { surveyTeamMemberDisplayLabel } from '../../../../api/strapiCrm';
import {
  groupSurveyTeamsByScheduledDate,
  type TeamPresenceRowState,
} from './startSurveyTeamPresence';

type Props = {
  teams: DynamicEntityRow[];
  teamPresence: Record<string, TeamPresenceRowState>;
  onTeamPresenceChange: (next: Record<string, TeamPresenceRowState>) => void;
  /** Prefix data-testid untuk checkbox hadir, mis. `survey-berita-acara-start-presence`. */
  presenceTestIdPrefix?: string;
};

export function StartSurveyTeamPresenceByDateTabs({
  teams,
  teamPresence,
  onTeamPresenceChange,
  presenceTestIdPrefix,
}: Props) {
  const dateGroups = useMemo(() => groupSurveyTeamsByScheduledDate(teams), [teams]);
  const [activeDateKey, setActiveDateKey] = useState<string | null>(null);

  useEffect(() => {
    if (!dateGroups.length) {
      setActiveDateKey(null);
      return;
    }
    if (!activeDateKey || !dateGroups.some((group) => group.dateKey === activeDateKey)) {
      setActiveDateKey(dateGroups[0]!.dateKey);
    }
  }, [dateGroups, activeDateKey]);

  const activeGroup =
    dateGroups.find((group) => group.dateKey === activeDateKey) ?? dateGroups[0] ?? null;

  if (!activeGroup) return null;

  return (
    <Box sx={{ mb: 2 }} data-testid="survey-start-team-presence-by-date">
      <Tabs
        value={activeGroup.dateKey}
        onChange={(_, value: string) => setActiveDateKey(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 40,
          borderBottom: 1,
          borderColor: 'divider',
          mb: 1,
          '& .MuiTab-root': { minHeight: 40, textTransform: 'none', py: 0.75 },
        }}
        data-testid="survey-start-team-date-tabs"
      >
        {dateGroups.map((group) => (
          <Tab
            key={group.dateKey}
            value={group.dateKey}
            label={group.tabLabel}
            data-testid={`survey-start-team-date-tab-${group.dateKey}`}
          />
        ))}
      </Tabs>

      <Box sx={{ overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nama</TableCell>
              <TableCell padding="checkbox" sx={{ width: 72 }}>
                Hadir
              </TableCell>
              <TableCell sx={{ minWidth: 140 }}>Foto (opsional)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeGroup.teams.map((team) => {
              const teamDocId = String(team.documentId ?? '').trim();
              const st = teamPresence[teamDocId] ?? {
                isPresence: false,
                pictureFile: null,
              };
              return (
                <TableRow key={teamDocId} hover>
                  <TableCell>{surveyTeamMemberDisplayLabel(team)}</TableCell>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={st.isPresence}
                      data-testid={
                        presenceTestIdPrefix
                          ? `${presenceTestIdPrefix}-${teamDocId}`
                          : undefined
                      }
                      onChange={(e) =>
                        onTeamPresenceChange({
                          ...teamPresence,
                          [teamDocId]: { ...st, isPresence: e.target.checked },
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      disabled={!st.isPresence}
                    >
                      Foto
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        capture="user"
                        onChange={(e) =>
                          onTeamPresenceChange({
                            ...teamPresence,
                            [teamDocId]: {
                              ...st,
                              pictureFile: e.target.files?.[0] ?? null,
                            },
                          })
                        }
                      />
                    </Button>
                    {st.pictureFile ? (
                      <Typography variant="caption" display="block">
                        {st.pictureFile.name}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
